import { Popup } from '@paywithextend/components'
import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectPuttingVirtualCardCancelFromState,
  selectTopLevelErrorFromState,
} from 'src/app/DeactivateVirtualCardContent/redux/deactivateVirtualCardContent.selectors'
import { selectReceiptAttachmentModelsList } from 'src/app/UploadReceiptsAutoMatchPopup/redux/uploadReceiptsAutoMatch.selectors'
import { createSaveSelectedVirtualCardIdsAction } from 'src/app/VirtualCardsAllCards/redux/virtualCardsAllCards.actions'
import CancelOrDeactivateVirtualCardsPopup from 'src/components/CancelOrDeactivateVirtualCardsPopup/CancelOrDeactivateVirtualCardsPopup'
import NotFoundPopupContent from 'src/components/NotFoundPopupContent/NotFoundPopupContent'
import { useFlyoutDispatch } from 'src/hooks'
import Trns from 'src/localization/Trns'
import { VirtualCardListModel } from 'src/models/virtualCard.models'
import { closeModal } from 'src/navigation/Lnk'
import { putVirtualCardCancelAndCloseModalOrRedirect } from 'src/redux/actions/data/virtualCards.actions'
import { selectSelectedVirtualCardIdsFromState } from 'src/redux/selectors/data/virtualCards.selectors'
import { toDollars } from 'src/utils/currency.utils'

export interface DeactivateVirtualCardContentProps {
  virtualCard: VirtualCardListModel | undefined
}

const DeactivateVirtualCardContent: React.FC<
  DeactivateVirtualCardContentProps
> = ({ virtualCard }) => {
  const puttingVirtualCardCancel = useSelector(
    selectPuttingVirtualCardCancelFromState,
  )
  const topLevelError = useSelector(selectTopLevelErrorFromState)
  const { analytics } = useAnalyticsContext()
  const selectedVirtualCardIdsFromRedux = useSelector(
    selectSelectedVirtualCardIdsFromState,
  )
  const receiptAttachments = useSelector(selectReceiptAttachmentModelsList)

  const dispatch = useFlyoutDispatch()

  return (
    <>
      {virtualCard !== undefined && !virtualCard.isInactive ? (
        <CancelOrDeactivateVirtualCardsPopup
          title={
            virtualCard.isPending ? (
              <Trns source={'Cancel Request'} />
            ) : (
              <Trns source={'Deactivate'} />
            )
          }
          subtitle={
            virtualCard.isPending ? (
              <Trns
                source={'Cancel new card request for ${virtualCardName}?'}
                expressions={{
                  virtualCardName: (
                    <span>{virtualCard?.truncatedDisplayName}</span>
                  ),
                }}
              />
            ) : (
              <Trns
                source={'Deactivate the virtual card ${virtualCardName}?'}
                expressions={{
                  virtualCardName: (
                    <span>{virtualCard?.truncatedDisplayName}</span>
                  ),
                }}
              />
            )
          }
          actionButtonLabel={
            virtualCard.isPending ? (
              <Trns source={'Yes, Cancel Request'} />
            ) : (
              <Trns source={'Deactivate'} />
            )
          }
          actionButtonLoading={puttingVirtualCardCancel}
          actionButtonOnClick={(): void => {
            dispatch(
              putVirtualCardCancelAndCloseModalOrRedirect(
                virtualCard.id,
                virtualCard.isPending,
              ),
            )
            if (
              selectedVirtualCardIdsFromRedux !== undefined &&
              selectedVirtualCardIdsFromRedux.length > 0
            ) {
              const selectedVirtualCardIds =
                selectedVirtualCardIdsFromRedux.filter(
                  (item) => item !== virtualCard.id,
                )
              dispatch(
                createSaveSelectedVirtualCardIdsAction(selectedVirtualCardIds),
              )
            }
            const eventName = virtualCard.isPending
              ? 'cancel vcn request'
              : 'deactivate card modal - deactivate'
            const properties = virtualCard.isPending
              ? {
                  type_of_card: virtualCard.cardTypeLabel.props.source,
                  card_name: virtualCard.displayName,
                  credit_limit: toDollars(virtualCard.balanceCents),
                  active_until:
                    virtualCard.activeUntilTimezonedMoment.format('YYYY-MM-DD'),
                  add_attachments:
                    receiptAttachments !== undefined &&
                    receiptAttachments.length > 0,
                  add_notes: virtualCard.notes !== undefined,
                }
              : {
                  bulk: false,
                  number_of_cards: 1,
                }
            analytics?.track(eventName, properties)
          }}
          cancelButtonLabel={
            virtualCard.isPending ? (
              <Trns source={'No, Keep Request'} />
            ) : (
              <Trns source={'Cancel'} />
            )
          }
          cancelButtonOnClick={(): void => {
            dispatch(closeModal())
            analytics?.track(
              virtualCard.isPending
                ? 'cancel vcn request - keep request'
                : 'deactivate card modal - cancel',
              {
                bulk: false,
                number_of_cards: 1,
              },
            )
          }}
          topLevelError={topLevelError}
        />
      ) : (
        <Popup
          onClickClose={(): void => {
            dispatch(closeModal())
          }}
          showCloseButton
        >
          <NotFoundPopupContent />
        </Popup>
      )}
    </>
  )
}

export default DeactivateVirtualCardContent
